import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken, clearToken, getToken } from "lib/api";
import { useI18n } from "i18n";

const AuthContext = createContext(null);

// The token lasts 20 min. Refresh every 10 min (big buffer) and also when the
// tab regains focus after being idle, so background-throttled timers don't let
// it lapse. A single transient failure must NOT stop the renewal loop.
const RENEW_INTERVAL_MS = 10 * 60 * 1000;
const REFRESH_ON_FOCUS_AFTER_MS = 8 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setLanguage, resetLanguage } = useI18n();
  const renewTimer = useRef(null);
  const lastRefreshRef = useRef(0);

  const stopRenewal = useCallback(() => {
    if (renewTimer.current) {
      clearInterval(renewTimer.current);
      renewTimer.current = null;
    }
  }, []);

  const forceLogout = useCallback(() => {
    stopRenewal();
    clearToken();
    setUser(null);
  }, [stopRenewal]);

  const renewToken = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await api.post("/api/auth/refresh/");
      if (res.data?.token) {
        setToken(res.data.token);
        lastRefreshRef.current = Date.now();
      }
    } catch (err) {
      // Only a definitive 401 (token already expired/invalid) logs out. Transient
      // or network errors keep the loop alive to retry on the next tick/focus.
      if (err.response?.status === 401) forceLogout();
    }
  }, [forceLogout]);

  const startRenewal = useCallback(() => {
    stopRenewal();
    // Refresh right away: a token restored from localStorage may already be close
    // to its 20-min expiry, so we can't wait a full interval to renew it.
    renewToken();
    renewTimer.current = setInterval(renewToken, RENEW_INTERVAL_MS);
  }, [renewToken, stopRenewal]);

  // Refresh when the tab becomes visible/focused again if the token is stale —
  // covers background timer throttling and short suspensions.
  useEffect(() => {
    const maybeRefresh = () => {
      if (
        document.visibilityState === "visible" &&
        getToken() &&
        Date.now() - lastRefreshRef.current > REFRESH_ON_FOCUS_AFTER_MS
      ) {
        renewToken();
      }
    };
    document.addEventListener("visibilitychange", maybeRefresh);
    window.addEventListener("focus", maybeRefresh);
    return () => {
      document.removeEventListener("visibilitychange", maybeRefresh);
      window.removeEventListener("focus", maybeRefresh);
    };
  }, [renewToken]);

  const fetchUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await api.get("/api/auth/me/");
      setUser(res.data);
      if (res.data?.language) setLanguage(res.data.language);
      startRenewal();
      return res.data;
    } catch {
      setUser(null);
      clearToken();
      return null;
    } finally {
      setLoading(false);
    }
  }, [startRenewal]);

  useEffect(() => {
    fetchUser();
    return stopRenewal;
  }, [fetchUser, stopRenewal]);

  const loginUser = useCallback((userData, newToken) => {
    setToken(newToken);
    setUser(userData);
    if (userData?.language) setLanguage(userData.language);
    startRenewal();
  }, [startRenewal, setLanguage]);

  const logout = useCallback(() => {
    stopRenewal();
    clearToken();
    setUser(null);
    resetLanguage();
    navigate("/liveness/login");
  }, [navigate, stopRenewal, resetLanguage]);

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
